import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [tiers, setTiers] = useState({});
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  const fetchTiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/tiers`);
      setTiers(res.data);
    } catch (error) {
      console.error("fetchTiers failed:", error);
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
  }, [fetchTiers]);

  const handleSubscribe = async (tier, billing = billingPeriod) => {
    if (!user) {
      toast.error("Please sign in first");
      navigate("/");
      return;
    }

    if (tier === "free") {
      toast.info("You're already on the free tier");
      return;
    }

    setSubscribing(tier);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/subscription/create-checkout`,
        { origin_url: window.location.origin, tier, billing_period: billing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (error) {
      console.error("checkout failed:", error);
      toast.error("Failed to create checkout session");
      setSubscribing("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-amber-200/80 text-sm pulse uppercase tracking-[0.4em] cinzel">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white cosmos-bg relative overflow-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-sm border-b border-amber-200/15">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="text-xs uppercase tracking-[0.25em] text-amber-200/80 hover:text-amber-200 transition-colors font-light flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Home
          </button>
        </div>
      </nav>

      <div className="relative z-10 pt-32 pb-16 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="cinzel text-[10px] tracking-[0.5em] text-amber-200/60 mb-4">
              — Sacred Pathways —
            </p>
            <h1 className="script-font text-6xl md:text-7xl gold-text gold-glow mb-6">
              Choose Your Plan
            </h1>
            <p className="text-xs text-amber-200/50 uppercase tracking-[0.4em] mb-10 cinzel">
              Start free · Upgrade anytime
            </p>

            <BillingToggle period={billingPeriod} setPeriod={setBillingPeriod} />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.free && (
              <TierCard
                tier={tiers.free}
                tierKey="free"
                accent="muted"
                price="€0"
                priceLabel="Forever"
                ctaLabel="Current Plan"
                ctaDisabled={true}
              />
            )}

            {tiers.premium && (
              <TierCard
                tier={tiers.premium}
                tierKey="premium"
                accent="popular"
                badge="Most Popular"
                price={billingPeriod === "monthly" ? `€${tiers.premium.price}` : `€${tiers.premium.annual_price}`}
                priceLabel={billingPeriod === "monthly" ? "Per Month" : "Per Year"}
                savings={billingPeriod === "annual" ? `Save €${tiers.premium.annual_savings}` : null}
                ctaLabel={getCtaLabel(subscribing, user, "premium")}
                ctaDisabled={subscribing === "premium" || user?.subscription_tier === "premium"}
                onClick={() => handleSubscribe("premium")}
              />
            )}

            {tiers.pro && (
              <TierCard
                tier={tiers.pro}
                tierKey="pro"
                accent="default"
                price={billingPeriod === "monthly" ? `€${tiers.pro.price}` : `€${tiers.pro.annual_price}`}
                priceLabel={billingPeriod === "monthly" ? "Per Month" : "Per Year"}
                savings={billingPeriod === "annual" ? `Save €${tiers.pro.annual_savings}` : null}
                ctaLabel={getCtaLabel(subscribing, user, "pro")}
                ctaDisabled={subscribing === "pro" || user?.subscription_tier === "pro"}
                onClick={() => handleSubscribe("pro")}
              />
            )}

            {tiers.lifetime && (
              <TierCard
                tier={tiers.lifetime}
                tierKey="lifetime"
                accent="lifetime"
                badge="Best Value"
                price={`€${tiers.lifetime.price}`}
                priceLabel="One-Time"
                savings="Pay once · Own forever"
                ctaLabel={
                  subscribing === "lifetime"
                    ? "Processing..."
                    : user?.subscription_tier === "lifetime"
                    ? "Active"
                    : "Buy Lifetime"
                }
                ctaDisabled={subscribing === "lifetime" || user?.subscription_tier === "lifetime"}
                onClick={() => handleSubscribe("lifetime", "lifetime")}
              />
            )}
          </div>

          <div className="mt-16 text-center">
            <p className="text-[10px] text-amber-200/40 uppercase tracking-[0.4em] cinzel">
              Cancel anytime · Secure Stripe payment · Instant access
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const getCtaLabel = (subscribing, user, tier) => {
  if (subscribing === tier) return "Processing...";
  if (user?.subscription_tier === tier) return "Current Plan";
  return "Subscribe";
};

const BillingToggle = ({ period, setPeriod }) => (
  <div className="inline-flex items-center space-x-2 bg-black/60 border border-amber-200/25 rounded-full p-1">
    <button
      data-testid="monthly-toggle"
      onClick={() => setPeriod("monthly")}
      className={`px-6 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] transition-all cinzel ${
        period === "monthly"
          ? "gold-btn"
          : "text-amber-200/50 hover:text-amber-200"
      }`}
    >
      Monthly
    </button>
    <button
      data-testid="annual-toggle"
      onClick={() => setPeriod("annual")}
      className={`px-6 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] transition-all cinzel ${
        period === "annual"
          ? "gold-btn"
          : "text-amber-200/50 hover:text-amber-200"
      }`}
    >
      Annual <span className="text-emerald-400 ml-1 normal-case">Save 20%</span>
    </button>
  </div>
);

const accentStyles = {
  muted: {
    wrapper: "border border-amber-200/15 hover:border-amber-200/30",
    title: "text-amber-200/70",
    check: "text-amber-200/40",
    feature: "text-gray-500",
    price: "text-amber-200/80",
    cta: "border border-amber-200/20 bg-transparent text-amber-200/50 cursor-default",
  },
  default: {
    wrapper: "border border-amber-200/25 hover:border-amber-200/50",
    title: "text-amber-100",
    check: "text-amber-200/80",
    feature: "text-gray-300",
    price: "gold-text",
    cta: "gold-outline-btn",
  },
  popular: {
    wrapper: "border-2 border-amber-200/60 hover:border-amber-200 transform md:scale-105 shadow-[0_0_40px_rgba(228,178,89,0.15)]",
    title: "gold-text",
    check: "text-amber-200",
    feature: "text-gray-200",
    price: "gold-text gold-glow",
    cta: "gold-btn",
  },
  lifetime: {
    wrapper: "border-2 border-amber-300/70 hover:border-amber-300 shadow-[0_0_50px_rgba(228,178,89,0.25)] bg-gradient-to-b from-amber-950/30 to-black",
    title: "gold-text",
    check: "text-amber-300",
    feature: "text-amber-50/90",
    price: "gold-text gold-glow",
    cta: "gold-btn",
  },
};

const TierCard = ({
  tier,
  tierKey,
  accent,
  badge,
  price,
  priceLabel,
  savings,
  ctaLabel,
  ctaDisabled,
  onClick,
}) => {
  const styles = accentStyles[accent];

  return (
    <div
      data-testid={`tier-${tierKey}`}
      className={`relative p-8 transition-all bg-black/40 backdrop-blur-sm ${styles.wrapper}`}
    >
      {badge && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 gold-btn px-4 py-1 text-[10px] uppercase tracking-[0.3em] font-medium cinzel">
          {badge}
        </div>
      )}

      <div className="text-center mb-8">
        <h3 className={`cinzel text-sm tracking-[0.3em] uppercase mb-4 ${styles.title}`}>
          {tier.name}
        </h3>
        <div className={`text-5xl font-light mb-2 ${styles.price}`}>{price}</div>
        <div className="text-[10px] text-amber-200/40 uppercase tracking-[0.3em] cinzel">
          {priceLabel}
        </div>
        {savings && (
          <div className="text-[10px] text-emerald-400 mt-2 uppercase tracking-wider cinzel">
            {savings}
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 min-h-[180px]">
        {tier.features.map((feature) => (
          <li key={feature} className="flex items-start space-x-3 text-xs">
            <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${styles.check}`} />
            <span className={`font-light ${styles.feature}`}>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        data-testid={`subscribe-${tierKey}-btn`}
        onClick={onClick}
        disabled={ctaDisabled}
        className={`w-full py-5 text-[10px] uppercase tracking-[0.3em] font-medium cinzel ${styles.cta}`}
      >
        {ctaLabel}
      </Button>
    </div>
  );
};

export default PricingPage;
